import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { adminApi } from "./api";
import { toast } from "sonner";
import { Send, CheckCircle2, RotateCcw, Loader2 } from "lucide-react";

const fmtTime = (s) =>
  s ? new Date(s).toLocaleString("en-CA", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

const Bubble = ({ mine, who, text, time }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-[#2340b5] text-white" : "border border-[#12142b]/8 bg-white text-[#12142b]"}`}>
      <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wide opacity-70">{who}</p>
      <p className="whitespace-pre-wrap break-words">{text}</p>
      <p className={`mt-1 text-[10px] ${mine ? "text-white/70" : "text-[#565b73]"}`}>{fmtTime(time)}</p>
    </div>
  </div>
);

export function TicketDialog({ caseItem, onClose, onChanged }) {
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const id = caseItem?.id;

  const load = useCallback(() => {
    if (!id) return;
    adminApi.get(`/support/tickets/${id}`).then((r) => setThread(r.data)).catch(() => setThread({ messages: [] }));
  }, [id]);
  useEffect(() => { setThread(null); setReply(""); load(); }, [load]);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await adminApi.post(`/support/tickets/${id}/reply`, { message: reply.trim() });
      setReply("");
      toast.success("Reply sent to user");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await adminApi.patch(`/support/tickets/${id}`, { status });
      toast.success(status === "resolved" ? "Ticket marked resolved" : "Ticket reopened");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const status = thread?.status || caseItem?.status;

  return (
    <Dialog open={!!caseItem} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="ticket-dialog">
        <DialogHeader>
          <DialogTitle>{caseItem?.subject}</DialogTitle>
          <p className="text-sm text-[#565b73]">
            {caseItem?.case_id} · {caseItem?.user_name} · {caseItem?.issue_type}
          </p>
          {caseItem?.trip && (caseItem.trip.route || caseItem.trip.trip_code) && (
            <p className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#2340b5]/8 px-3 py-1 text-xs font-semibold text-[#2340b5]" data-testid="ticket-dialog-trip">
              {caseItem.trip.trip_code ? `${caseItem.trip.trip_code} · ` : ""}{caseItem.trip.route || "Linked trip"}{caseItem.trip.departure ? ` · ${caseItem.trip.departure}` : ""}
            </p>
          )}
        </DialogHeader>

        <div className="max-h-[42vh] space-y-3 overflow-y-auto rounded-xl bg-[#f4f6fb] p-4" data-testid="ticket-thread">
          {!thread && <p className="py-6 text-center text-sm text-[#565b73]">Loading conversation…</p>}
          {thread?.messages?.map((m, i) => (
            <Bubble key={i} mine={m.from === "support"} who={m.from === "support" ? "Support" : "User"} text={m.text} time={m.at} />
          ))}
          {thread && thread.messages?.length === 0 && (
            <p className="py-6 text-center text-sm text-[#565b73]">No messages yet.</p>
          )}
        </div>

        {status !== "resolved" ? (
          <div className="space-y-2">
            <textarea
              data-testid="ticket-reply-input"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Write a reply…"
              className="w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]"
            />
            <div className="flex flex-wrap items-center gap-2">
              <button
                data-testid="ticket-send-reply"
                onClick={send}
                disabled={sending || !reply.trim()}
                className="flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1b3390] disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Reply
              </button>
              <button
                data-testid="ticket-resolve"
                onClick={() => setStatus("resolved")}
                className="flex items-center gap-2 rounded-xl border border-[#16a34a]/40 px-4 py-2.5 text-sm font-semibold text-[#16a34a] transition-colors hover:bg-[#16a34a]/8"
              >
                <CheckCircle2 className="h-4 w-4" /> Mark Resolved
              </button>
            </div>
          </div>
        ) : (
          <button
            data-testid="ticket-reopen"
            onClick={() => setStatus("open")}
            className="flex items-center gap-2 self-start rounded-xl border border-[#2340b5]/30 px-4 py-2.5 text-sm font-semibold text-[#2340b5] transition-colors hover:bg-[#2340b5]/8"
          >
            <RotateCcw className="h-4 w-4" /> Reopen Ticket
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ChatDialog({ chat, onClose }) {
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [recipient, setRecipient] = useState(null);
  const [sending, setSending] = useState(false);
  const id = chat?.id;

  const load = useCallback(() => {
    if (!id) { setThread(null); return; }
    adminApi.get(`/support/chats/${id}`)
      .then((r) => setThread(r.data))
      .catch(() => setThread({ messages: [], rider: {}, driver: {} }));
  }, [id]);
  useEffect(() => { setThread(null); setReply(""); setRecipient(null); load(); }, [load]);

  // Valid reply targets = participants that are real users (exclude the admin service / self).
  const targets = [];
  if (thread?.rider?.id && thread.rider.id !== "svc-admin-write")
    targets.push({ key: "rider", id: thread.rider.id, name: thread.rider.name || "Rider" });
  if (thread?.driver?.id && thread.driver.id !== "svc-admin-write")
    targets.push({ key: "driver", id: thread.driver.id, name: thread.driver.name || "Driver" });

  useEffect(() => {
    if (targets.length && !targets.find((t) => t.key === recipient)) setRecipient(targets[0].key);
  }, [thread]);

  const target = targets.find((t) => t.key === recipient);

  const send = async () => {
    const text = reply.trim();
    if (!text || !target) return;
    setSending(true);
    try {
      await adminApi.post(`/users/${target.id}/message`, { text });
      setThread((d) => ({
        ...d,
        messages: [...(d?.messages || []), {
          id: `admin-${Date.now()}`, sender: "admin", text, created_at: new Date().toISOString(),
        }],
      }));
      setReply("");
      toast.success(`Reply sent to ${target.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!chat} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="chat-dialog">
        <DialogHeader>
          <DialogTitle>{chat?.route || "Conversation"}</DialogTitle>
          <p className="text-sm text-[#565b73]">
            {chat?.rider_name} ↔ {chat?.driver_name}
          </p>
        </DialogHeader>
        <div className="max-h-[48vh] space-y-3 overflow-y-auto rounded-xl bg-[#f4f6fb] p-4" data-testid="chat-thread">
          {!thread && <p className="py-6 text-center text-sm text-[#565b73]">Loading conversation…</p>}
          {thread?.messages?.map((m) => {
            const isAdmin = m.sender === "admin" || m.sender === "support";
            const isDriver = !isAdmin && m.sender === "driver";
            return (
              <Bubble
                key={m.id}
                mine={isAdmin || isDriver}
                who={isAdmin ? "SafeRide Support" : isDriver ? (thread.driver?.name || "Driver") : (thread.rider?.name || "Rider")}
                text={m.text}
                time={m.created_at}
              />
            );
          })}
          {thread && thread.messages?.length === 0 && (
            <p className="py-6 text-center text-sm text-[#565b73]">No messages in this conversation.</p>
          )}
        </div>

        {thread && targets.length > 0 && (
          <div className="space-y-2" data-testid="chat-reply-box">
            {targets.length > 1 && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-[#565b73]">Reply to:</span>
                <div className="inline-flex overflow-hidden rounded-lg border border-[#12142b]/12">
                  {targets.map((t) => (
                    <button
                      key={t.key}
                      data-testid={`chat-reply-to-${t.key}`}
                      onClick={() => setRecipient(t.key)}
                      className={`px-3 py-1.5 font-semibold transition-colors ${recipient === t.key ? "bg-[#2340b5] text-white" : "bg-white text-[#565b73] hover:bg-[#f4f6fb]"}`}
                    >
                      {t.key === "driver" ? "Driver" : "Rider"} ({t.name})
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-reply-input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
                rows={2}
                maxLength={1000}
                placeholder={target ? `Reply to ${target.name} as SafeRide Support…` : "Reply…"}
                className="flex-1 resize-none rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]"
              />
              <button
                data-testid="chat-reply-send"
                disabled={sending || !reply.trim() || !target}
                onClick={send}
                className="flex h-11 items-center gap-2 rounded-xl bg-[#2340b5] px-4 text-sm font-bold text-white transition-colors hover:bg-[#1b3390] disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
